import React, { PropsWithChildren } from 'react';

import { appReactMemo } from 'hocs';

import { useFilterContext } from '../../Filters/FilterContext';
import { useOverflowCell } from '../../hooks/useOverflowCell';
import { HelperPopover, HelperPopoverContainer, RowCellContainer } from '../../styled';

import { Text } from './styled';

export interface TRowCellData<T> {
  center?: boolean;
  children?: React.ReactNode;
  className?: string;
  dataKey?: keyof T;
  isEmpty?: boolean;
  isSelected?: boolean;
  item: T;
  onClick?: (item: T) => void;
  rowIndex: number;
}

type TRowCellProps<T> = PropsWithChildren<TRowCellData<T>>;

const RowCell = <T,>({
  center,
  children,
  className,
  dataKey,
  isEmpty,
  isSelected,
  item,
  onClick,
  rowIndex,
}: TRowCellProps<T>): React.ReactElement => {
  const {
    config: { mainColumnsList },
  } = useFilterContext();

  const { isOverflowed, ref } = useOverflowCell<HTMLDivElement>();

  const cellData =
    dataKey &&
    dataKey
      .toString()
      .split('.')
      .reduce<string | T>(
        (accumulator, currentValue) => accumulator?.[currentValue as never] || '-',
        item,
      );

  const onRowClick = () => {
    return onClick?.(item);
  };

  const renderContent = () => {
    if (isEmpty) return '';
    if (children) return children;

    if (typeof cellData !== 'object' && cellData !== '') {
      return <>{cellData ?? '-'}</>;
    }

    return <>-</>;
  };

  const content = renderContent();

  return (
    <RowCellContainer
      $center={center}
      $index={rowIndex}
      $isMainColumn={mainColumnsList?.some((column) => column === dataKey)}
      $isPointer={!!onClick || isOverflowed}
      $isSelected={isSelected}
      className={className}
      data-testid={`${dataKey as string}Cell`}
      onClick={onRowClick}
    >
      <HelperPopover
        asContainer={HelperPopoverContainer}
        component={content}
        disabled={!isOverflowed}
      >
        <Text ref={ref}>{content}</Text>
      </HelperPopover>
    </RowCellContainer>
  );
};

export default appReactMemo(RowCell);
